/**
 * Les lectures de l'onglet « Offres de cours » : les compteurs (`getProposalsCount`) et la liste de
 * l'onglet montré, page après page (15 par page) — `getGroupAttributionProposals` (`is_trial` 0 ou 1)
 * ou `getSubstitutionProposals`, rangées dans le compte par `currentUser` (page 1 remplace, la suite
 * s'ajoute).
 *
 * Comme `ProposalsTabScreen` : tout est relu à chaque retour sur l'onglet et à chaque changement
 * d'onglet (l'ancienne liste se remontait). Chaque lecture d'une liste marque le masteur « actif »
 * côté serveur (`last_proposals_viewed_at`) : c'est voulu, c'est l'onglet qu'il regarde.
 *
 * Le curseur vient de la `meta` de la réponse, gardé ici : les compteurs réécrivent les `*_meta` du
 * compte sans pagination. Une réponse n'est lue que si elle répond à NOTRE dernière demande (l'onglet
 * et la page : `scopeUrlParams`) — la page d'une offre ouverte par son lien lit la même liste.
 */
import { useFocusEffect } from "@react-navigation/native"
import { useCallback, useRef, useState } from "react"
import { useDispatch } from "react-redux"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { groupAttributionProposalsService } from "../../../../../../data/services/groupAttributionProposalsService"
import { groupAttributionService } from "../../../../../../data/services/groupAttributionService"
import { substitutionProposalsService } from "../../../../../../data/services/substitutionProposalsService"
import { useApiPagedFetch } from "../../../hooks/useApiPagedFetch"
import { type UITutorOfferKind } from "../../../types/tutorDispoOffres"

const PER_PAGE = 15

const urlParamsOf = (kind: UITutorOfferKind) => ({ per_page: PER_PAGE, is_trial: kind === "trial" ? 1 : 0 })

export const TUTOR_OFFERS_MORE_ERROR = "Impossible de charger la suite."

/**
 * `hasOffers` : des offres sont déjà en main. Sans elles, la première lecture montre la forme des
 * offres (`listLoading`), jamais « Aucune offre pour le moment » avant que la liste n'arrive
 * (03/11) ; avec elles, c'est une relecture : la flèche tourne (`refreshing`).
 */
export function useTutorOffersData(kind: UITutorOfferKind, hasOffers: boolean) {
    const dispatch = useDispatch()
    /** L'onglet vient de changer : sa liste charge, le haut reste. */
    const [switching, setSwitching] = useState(false)

    const groups = useApiPagedFetch({
        service: groupAttributionProposalsService.getGroupAttributionProposals,
        scopeUrlParams: ["is_trial"],
        fetchOnMount: false,
    })
    const substitutions = useApiPagedFetch({
        service: substitutionProposalsService.getSubstitutionProposals,
        fetchOnMount: false,
    })
    const list = kind === "substitution" ? substitutions : groups
    // La liste de l'autre porte, encore en route d'un onglet quitté, ne doit pas déplacer le curseur.
    const other = kind === "substitution" ? groups : substitutions
    const refreshList = list.refresh
    const cancelOther = other.cancel

    const fetchedKindRef = useRef(kind)
    const refresh = useCallback(() => {
        dispatch(apiRequest(groupAttributionService.getProposalsCount) as never)
        setSwitching(fetchedKindRef.current !== kind)
        fetchedKindRef.current = kind
        cancelOther()
        refreshList(urlParamsOf(kind))
    }, [dispatch, kind, refreshList, cancelOther])

    // Au retour sur l'onglet, et à chaque changement d'onglet tant qu'il est montré.
    useFocusEffect(refresh)

    return {
        refresh,
        loadMore: list.loadMore,
        hasMore: list.hasMore || list.loadMoreFailed,
        loadingMore: list.loadingMore,
        loadMoreError: list.loadMoreFailed ? TUTOR_OFFERS_MORE_ERROR : null,
        listLoading: list.firstPageLoading && (switching || !hasOffers),
        refreshing: list.firstPageLoading && !switching && hasOffers,
        /** La première page a échoué : sans offre en main, la page le dit. */
        firstPageFailed: list.firstPageFailed,
    }
}
